/**
 * File: web/src/components/UploadQueue.tsx
 * =======================================
 *
 * Multipart upload queue UI + orchestration (browser → S3)
 *
 * Purpose:
 * - Provide a multi-file upload experience with:
 *   - file picker + drag-and-drop
 *   - per-file progress + status
 *   - pause/resume (same multipart session)
 *   - cancel (best-effort server-side abort)
 *   - retry (fresh multipart session)
 *
 * When used:
 * - Rendered by the Upload route after the user is signed in.
 * - Receives runtime AppConfig so it knows:
 *   - allowed MIME types (UI validation hints)
 *   - apiBaseUrl for initiate/part-url/complete/abort endpoints
 *
 * High-level data flow (per file):
 * 1) Initiate (API) → creates multipart upload session and returns { fileId, partSizeBytes }
 * 2) For each part:
 *    - Get presigned URL (API)
 *    - PUT part to S3 directly (XHR so we get progress events)
 *    - Capture ETag for completion
 * 3) Complete (API) → backend assembles parts into final object and updates session state
 *
 * Learner notes:
 * - Direct-to-S3 uploads keep Lambda/API Gateway out of the data path (cost + scale benefits).
 * - The client “knows” a lot here (part sizes, progress, concurrency), but the backend still
 *   enforces security and allowed file types. Client checks are UX; server checks are policy.
 *
 * Gotchas:
 * - Token expiry mid-upload: if API calls start returning 401, api.ts will clear token and redirect.
 * - Presigned URLs expire; long uploads may need re-signing or smaller parts (backend config).
 * - Multipart part numbers are 1-based (S3 convention). Completion requires correct part list order.
 */

import { useMemo, useRef, useState } from "react";
import type { AppConfig } from "../lib/types";
import { getToken } from "../lib/auth";
import {
  abortUpload,
  completeUpload,
  getPartUrl,
  initiateUpload,
  putPartWithXhr,
} from "../lib/upload";
import { useToast } from "./ToastProvider";

type Status =
  | "Queued"
  | "Initiating"
  | "Uploading"
  | "Paused"
  | "Finalizing"
  | "Done"
  | "Failed"
  | "Canceled";

/**
 * UI item represents one file in the queue.
 * - id is a local UI identifier (crypto.randomUUID).
 * - fileId is the backend "session id" used by multipart endpoints (returned by initiate).
 */
type Item = {
  id: string;
  file: File;
  status: Status;
  progress: number;
  error?: string;
  fileId?: string; // needed for cancel + resume (same-session)
};

/**
 * UploadController is the per-file "runtime brain" for the upload.
 *
 * Why separate controller state from React state:
 * - React state is great for rendering, but we also need mutable, frequently updated values
 *   (per-part progress, inflight XHR objects, etc.) without triggering re-renders on every byte.
 * - Using a ref + mutable maps/sets lets us:
 *   - update internal counters constantly
 *   - only update React state when we want to update the UI (progress percentage, status changes)
 */
type UploadController = {
  paused: boolean;
  canceled: boolean;

  fileId?: string;
  partSize?: number;
  totalParts?: number;

  doneParts: Set<number>;
  etags: Map<number, string>;

  // Progress that survives pause/resume and aborts
  completedBytes: number;

  // Inflight tracking so we can compute progress and abort XHRs
  inflightLoaded: Map<number, number>; // partNumber -> loaded bytes so far
  inflightXhrs: Map<number, XMLHttpRequest>; // partNumber -> xhr
  inflightParts: Set<number>; // partNumbers currently assigned/in progress
};

function formatBytes(n: number) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Client-side allowed type check.
 *
 * Why:
 * - Provides immediate feedback before initiating an upload (better UX).
 * - Still not a security boundary: backend enforces allowed types too.
 *
 * Implementation:
 * - Prefer MIME match using File.type when available.
 * - Fall back to extension inference because browsers sometimes provide empty/odd MIME types.
 */
function allowedByConfig(config: AppConfig, file: File) {
  const allowed = new Set(
    (config.allowedTypes || []).map((t) => t.toLowerCase()),
  );
  const mime = (file.type || "").toLowerCase();
  if (mime && allowed.has(mime)) return true;

  // fallback by extension
  const ext = file.name.split(".").pop()?.toLowerCase();
  const map: Record<string, string> = {
    zip: "application/zip",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
  };
  const inferred = ext ? map[ext] : undefined;
  return inferred ? allowed.has(inferred) : false;
}

export function UploadQueue(props: { config: AppConfig }) {
  const toast = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * controllersRef maps UI item id -> controller state.
   *
   * Why useRef(Map):
   * - We want controllers to persist across renders.
   * - We want to mutate controller internals without triggering re-renders.
   * - Each upload can spawn async workers; they need stable shared state.
   */
  const controllersRef = useRef<Map<string, UploadController>>(new Map());

  /**
   * Build the <input type="file" accept="..."> attribute from config.allowedTypes.
   *
   * Why:
   * - The file picker can filter selectable files, improving UX.
   * - This is advisory: drag/drop can still include non-accepted types, so we validate again.
   *
   * Note:
   * - We include both MIME types and extensions for better browser compatibility.
   */
  const accept = useMemo(() => {
    const types = props.config.allowedTypes || [];
    const parts = [...types];
    if (types.includes("application/zip")) parts.push(".zip");
    if (types.includes("image/jpeg")) parts.push(".jpg", ".jpeg");
    return Array.from(new Set(parts)).join(",");
  }, [props.config.allowedTypes]);

  /**
   * Compute progress percentage for the UI.
   *
   * How:
   * - completedBytes: sum of fully completed parts (stable across pause/resume)
   * - inflightLoaded: current bytes uploaded for each in-progress part (updated by XHR progress)
   */
  const computeProgressPct = (ctl: UploadController, fileSize: number) => {
    const inflight = Array.from(ctl.inflightLoaded.values()).reduce(
      (a, b) => a + b,
      0,
    );
    const totalUploaded = ctl.completedBytes + inflight;
    return Math.min(100, Math.floor((totalUploaded / fileSize) * 100));
  };

  /**
   * Part assignment helper for concurrency workers.
   *
   * Strategy:
   * - Scan from 1..totalParts and pick the first part that is neither done nor inflight.
   * - Mark it inflight immediately to prevent two workers from taking the same part.
   *
   * Learner note:
   * - This is a simple scheduler that works well for moderate part counts.
   * - It avoids needing a queue structure and keeps logic easy to reason about.
   */
  const takeNextPart = (ctl: UploadController): number | null => {
    const total = ctl.totalParts ?? 0;
    for (let p = 1; p <= total; p++) {
      if (!ctl.doneParts.has(p) && !ctl.inflightParts.has(p)) {
        ctl.inflightParts.add(p);
        return p;
      }
    }
    return null;
  };

  /**
   * Remove a file from the UI list.
   *
   * Important:
   * - This is "best-effort local abort". It does not necessarily abort the server-side multipart upload.
   * - For true cleanup we use cancelUpload(), which calls the backend abort endpoint.
   */
  const removeItem = (id: string) => {
    // Best-effort local abort if user removes while inflight
    const ctl = controllersRef.current.get(id);
    if (ctl) {
      ctl.canceled = true;

      // Abort any inflight part uploads immediately (stops network activity).
      for (const xhr of ctl.inflightXhrs.values()) xhr.abort();

      // Clear inflight tracking to keep controller consistent.
      ctl.inflightXhrs.clear();
      ctl.inflightLoaded.clear();
      ctl.inflightParts.clear();
    }
    controllersRef.current.delete(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  /**
   * Remove finished items (Done/Canceled/Failed) from the UI list.
   * This keeps the screen tidy after a batch.
   */
  const clearCompleted = () =>
    setItems((prev) =>
      prev.filter((x) => !["Done", "Canceled", "Failed"].includes(x.status)),
    );

  /**
   * Pause an upload:
   * - set paused flag so workers stop
   * - abort inflight XHRs so the browser stops uploading bytes immediately
   *
   * Learner note:
   * - Pausing does NOT destroy the server-side multipart session.
   *   We can resume within the same session (same fileId) by continuing remaining parts.
   */
  const pauseUpload = (id: string) => {
    const ctl = controllersRef.current.get(id);
    if (!ctl) return;

    ctl.paused = true;

    // Abort inflight parts to stop quickly
    for (const xhr of ctl.inflightXhrs.values()) xhr.abort();

    // Clear inflight maps; completedBytes and doneParts remain for resume.
    ctl.inflightXhrs.clear();
    ctl.inflightLoaded.clear();
    ctl.inflightParts.clear();

    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: "Paused" } : it)),
    );
    toast.push({
      kind: "info",
      title: "Upload paused",
      message: "You can resume any time (this session).",
    });
  };

  /**
   * Resume an upload:
   * - clears paused/canceled flags
   * - flips UI status to Uploading
   * - calls continueUpload() to restart workers for remaining parts
   *
   * Gotcha:
   * - Resume only works if we still have fileId/partSize/totalParts from the initiate step.
   *   Those are preserved in the controller (and fileId is also mirrored to UI state).
   */
  const resumeUpload = (id: string) => {
    const ctl = controllersRef.current.get(id);
    const item = items.find((x) => x.id === id);
    if (!ctl || !item) return;

    ctl.paused = false;
    ctl.canceled = false;

    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, status: "Uploading", error: undefined } : it,
      ),
    );

    void continueUpload(id, item.file);
    toast.push({
      kind: "info",
      title: "Resuming upload",
      message: item.file.name,
    });
  };

  /**
   * Cancel an upload:
   * - stop local inflight activity
   * - attempt server-side abort (best effort)
   * - mark UI as Canceled regardless of server abort outcome
   *
   * Why abort server-side:
   * - Without abort, partially uploaded parts can remain in S3 and incur storage cost.
   * - Your backend also applies a lifecycle rule to abort incomplete MPUs after 1 day,
   *   but explicit abort is cleaner and faster.
   */
  const cancelUpload = async (id: string) => {
    const ctl = controllersRef.current.get(id);
    const token = getToken();
    const item = items.find((x) => x.id === id);
    if (!ctl || !item) return;

    ctl.canceled = true;
    ctl.paused = false;

    // Abort inflight immediately
    for (const xhr of ctl.inflightXhrs.values()) xhr.abort();
    ctl.inflightXhrs.clear();
    ctl.inflightLoaded.clear();
    ctl.inflightParts.clear();

    // Abort multipart server-side (cleanup S3 multipart)
    try {
      if (token && item.fileId) {
        await abortUpload(props.config, token, item.fileId);
      }
    } catch (e: any) {
      // If server abort fails, we still consider it "canceled locally".
      toast.push({
        kind: "warning",
        title: "Canceled locally",
        message: `Could not abort on server: ${e?.message || "Error"}`,
      });
    }

    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, status: "Canceled", error: undefined } : it,
      ),
    );
    toast.push({
      kind: "warning",
      title: "Upload canceled",
      message: item.file.name,
    });
  };

  /**
   * continueUpload() performs the concurrent part uploading for a file.
   *
   * Requirements:
   * - token must exist (authenticated)
   * - controller must have fileId, partSize, totalParts (initiate step done)
   * - upload must not be paused/canceled
   *
   * Concurrency model:
   * - Spawn N async "workers"
   * - Each worker:
   *   - takes next available part
   *   - requests a presigned URL
   *   - uploads part to S3 (XHR)
   *   - records ETag and marks part done
   * - When all parts done: finalize via completeUpload()
   */
  const continueUpload = async (id: string, file: File) => {
    const token = getToken();
    if (!token) return;

    const ctl = controllersRef.current.get(id);
    if (!ctl) return;

    // If user paused/canceled, stop immediately.
    if (ctl.canceled || ctl.paused) return;

    // Initiate step must have run already.
    if (!ctl.fileId || !ctl.partSize || !ctl.totalParts) return;

    // Concurrency controls how many parts upload in parallel.
    // Increasing can improve speed but increases network pressure and S3 request rate.
    const concurrency = 4;

    const workers = Array.from({ length: concurrency }, async () => {
      while (true) {
        if (ctl.canceled || ctl.paused) return;

        // Pick the next unassigned part.
        const partNumber = takeNextPart(ctl);
        if (!partNumber) return;

        // Compute byte range for this part.
        const start = (partNumber - 1) * ctl.partSize!;
        const end = Math.min(start + ctl.partSize!, file.size);
        const blob = file.slice(start, end);

        try {
          // Step A: ask backend for a presigned URL for this specific part.
          const { url } = await getPartUrl(
            props.config,
            token,
            ctl.fileId!,
            partNumber,
          );

          /**
           * Step B: upload part bytes directly to S3.
           *
           * putPartWithXhr returns:
           * - xhr: so we can abort on pause/cancel
           * - promise: resolves to the part ETag required for completion
           */
          const { xhr, promise } = putPartWithXhr(url, blob, (loaded) => {
            // Update inflight loaded bytes for progress calculation.
            ctl.inflightLoaded.set(partNumber, loaded);

            // Update UI progress occasionally; this causes a re-render but only on progress events.
            const pct = computeProgressPct(ctl, file.size);
            setItems((prev) =>
              prev.map((it) => (it.id === id ? { ...it, progress: pct } : it)),
            );
          });

          // Track xhr so pause/cancel can abort it.
          ctl.inflightXhrs.set(partNumber, xhr);

          // Wait for this part upload to finish; resolves with ETag header value.
          const eTag = await promise;

          // Cleanup inflight tracking now that the part is done.
          ctl.inflightXhrs.delete(partNumber);
          ctl.inflightLoaded.delete(partNumber);
          ctl.inflightParts.delete(partNumber);

          // Mark completed state.
          ctl.doneParts.add(partNumber);
          ctl.etags.set(partNumber, eTag);

          // completedBytes represents finished parts; this makes progress stable across pause/resume.
          ctl.completedBytes += blob.size;

          // Update UI progress after finishing the part.
          const pct = computeProgressPct(ctl, file.size);
          setItems((prev) =>
            prev.map((it) => (it.id === id ? { ...it, progress: pct } : it)),
          );
        } catch (e: any) {
          // Cleanup inflight markers for this part so scheduler doesn't deadlock.
          ctl.inflightXhrs.delete(partNumber);
          ctl.inflightLoaded.delete(partNumber);
          ctl.inflightParts.delete(partNumber);

          // If pause/cancel caused abort, exit quietly (not a "failure").
          if (ctl.paused || ctl.canceled || e?.message === "Aborted") return;

          // Otherwise this is a real error (presign failed, S3 PUT failed, etc.).
          setItems((prev) =>
            prev.map((it) =>
              it.id === id
                ? { ...it, status: "Failed", error: e?.message || "Error" }
                : it,
            ),
          );
          return;
        }
      }
    });

    // Wait for all workers to drain available parts (or stop due to pause/cancel/failure).
    await Promise.all(workers);

    if (ctl.paused || ctl.canceled) return;

    /**
     * Finalization:
     * - Only when all parts are done do we call completeUpload().
     * - We build the ordered parts array expected by S3 CompleteMultipartUpload:
     *   [{ partNumber: 1, eTag: "..." }, ...]
     */
    if (ctl.doneParts.size === ctl.totalParts) {
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "Finalizing" } : it)),
      );

      const parts = [];
      for (let p = 1; p <= ctl.totalParts; p++) {
        parts.push({ partNumber: p, eTag: ctl.etags.get(p) || "" });
      }

      await completeUpload(props.config, token, ctl.fileId, parts);

      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, status: "Done", progress: 100 } : it,
        ),
      );
      toast.push({
        kind: "success",
        title: "Upload complete",
        message: file.name,
      });
    }
  };

  /**
   * startUpload() ensures we have an upload controller and initiates the multipart session once.
   *
   * Important behavior:
   * - Initiate is performed only once per controller so pause/resume reuses the same session.
   * - Retry intentionally discards the controller and starts a fresh session (new fileId).
   */
  const startUpload = async (id: string, file: File) => {
    const token = getToken();
    if (!token) return;

    // Controller is stored in controllersRef so it survives renders and is shared by async workers.
    let ctl = controllersRef.current.get(id);
    if (!ctl) {
      ctl = {
        paused: false,
        canceled: false,
        doneParts: new Set(),
        etags: new Map(),
        completedBytes: 0,
        inflightLoaded: new Map(),
        inflightXhrs: new Map(),
        inflightParts: new Set(),
      };
      controllersRef.current.set(id, ctl);
    }

    try {
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "Initiating" } : it)),
      );

      // Initiate only once so resume uses same multipart upload session
      if (!ctl.fileId) {
        const init = await initiateUpload(props.config, token, file);

        // fileId is our backend session id; partSizeBytes tells us how to chunk the file.
        ctl.fileId = init.fileId;
        ctl.partSize = init.partSizeBytes;

        // Total parts are computed client-side based on file size and part size.
        ctl.totalParts = Math.ceil(file.size / init.partSizeBytes);

        // Mirror fileId into UI state (used by cancel).
        setItems((prev) =>
          prev.map((it) =>
            it.id === id ? { ...it, fileId: init.fileId } : it,
          ),
        );
      }

      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, status: "Uploading", error: undefined } : it,
        ),
      );

      await continueUpload(id, file);
    } catch (e: any) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, status: "Failed", error: e?.message || "Error" }
            : it,
        ),
      );
      toast.push({
        kind: "error",
        title: "Upload failed",
        message: `${file.name}: ${e?.message || "Error"}`,
      });
    }
  };

  /**
   * Retry a failed upload:
   * - starts a fresh multipart session (new fileId)
   * - resets progress and error state
   *
   * Why not resume:
   * - If a failure happened due to a bad session state, starting fresh is often simpler.
   */
  const retry = (id: string) => {
    const item = items.find((x) => x.id === id);
    if (!item) return;

    // Retry starts a fresh multipart upload (new fileId)
    controllersRef.current.delete(id);

    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              status: "Queued",
              progress: 0,
              error: undefined,
              fileId: undefined,
            }
          : it,
      ),
    );

    void startUpload(id, item.file);
  };

  /**
   * Add files into the queue (from picker or drag/drop).
   *
   * For each file:
   * - Add to UI list immediately as Queued (optimistic UI)
   * - Validate auth and allowed types
   * - Start upload
   */
  const addFiles = async (files: FileList | File[]) => {
    const token = getToken();
    for (const file of Array.from(files)) {
      const id = crypto.randomUUID();

      // Add to UI first so the user sees the file appear immediately.
      setItems((prev) => [
        { id, file, status: "Queued", progress: 0 },
        ...prev,
      ]);

      if (!token) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? { ...it, status: "Failed", error: "Not signed in" }
              : it,
          ),
        );
        toast.push({
          kind: "warning",
          title: "Sign in required",
          message: "Please sign in before uploading.",
        });
        continue;
      }

      if (!allowedByConfig(props.config, file)) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? { ...it, status: "Failed", error: "Type not allowed" }
              : it,
          ),
        );
        toast.push({
          kind: "error",
          title: "File type not allowed",
          message: `${file.name} is not an allowed type.`,
        });
        continue;
      }

      // Kick off the upload asynchronously; UI remains responsive.
      void startUpload(id, file);
    }
  };

  const anyClearable = items.some(
    (x) =>
      x.status === "Done" || x.status === "Canceled" || x.status === "Failed",
  );

  return (
    <div>
      {/* Top controls: file picker button + allowed types + clear finished */}
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="row">
          {/* Hidden file input:
              - accept filters file picker options
              - onChange triggers addFiles()
          */}
          <input
            ref={inputRef}
            id="picker"
            type="file"
            multiple
            accept={accept}
            style={{ display: "none" }}
            onChange={(e) => e.target.files && void addFiles(e.target.files)}
          />

          {/* Clicking this button triggers the hidden file input */}
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => inputRef.current?.click()}
          >
            Choose files
          </button>

          {/* Display allowed types as a UX hint */}
          <span className="notice" style={{ padding: "10px 12px" }}>
            Allowed: {(props.config.allowedTypes || []).join(", ")}
          </span>
        </div>

        {anyClearable ? (
          <button
            className="btn btn-secondary"
            type="button"
            onClick={clearCompleted}
          >
            Clear finished
          </button>
        ) : null}
      </div>

      {/* Dropzone:
          - click to open file picker
          - drag/drop to add files
          - keyboard accessible (Enter/Space)
      */}
      <div
        className="dropzone"
        tabIndex={0}
        role="button"
        aria-label="Upload area"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLDivElement).classList.add("dragover");
        }}
        onDragLeave={(e) =>
          (e.currentTarget as HTMLDivElement).classList.remove("dragover")
        }
        onDrop={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLDivElement).classList.remove("dragover");
          if (e.dataTransfer.files) void addFiles(e.dataTransfer.files);
        }}
      >
        <p style={{ margin: 0, fontWeight: 700, color: "#2a2a2a" }}>
          Drag & drop files here
        </p>
        <p style={{ margin: "6px 0 0" }}>or click "Choose files".</p>
      </div>

      {/* File list */}
      <div>
        {items.map((it) => (
          <div key={it.id} className="file-item">
            <div className="file-top">
              <div>
                <div className="file-name">{it.file.name}</div>
                <div className="file-status">
                  {formatBytes(it.file.size)} • {it.status}
                  {it.error ? ` — ${it.error}` : ""}
                </div>
              </div>

              {/* Actions shown depend on current status */}
              <div
                className="row file-actions"
                style={{ justifyContent: "flex-end" }}
              >
                {it.status === "Uploading" ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => pauseUpload(it.id)}
                  >
                    Pause
                  </button>
                ) : null}

                {it.status === "Paused" ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => resumeUpload(it.id)}
                  >
                    Resume
                  </button>
                ) : null}

                {it.status === "Uploading" ||
                it.status === "Paused" ||
                it.status === "Initiating" ||
                it.status === "Finalizing" ? (
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={() => void cancelUpload(it.id)}
                  >
                    Cancel
                  </button>
                ) : null}

                {it.status === "Failed" ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => retry(it.id)}
                  >
                    Retry
                  </button>
                ) : null}

                {/* Remove after Done, Canceled, or Failed */}
                {it.status === "Done" ||
                it.status === "Canceled" ||
                it.status === "Failed" ? (
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={() => removeItem(it.id)}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </div>

            {/* Progress bar:
                width is driven by computed progress percentage for this file.
            */}
            <div className="progress">
              <div style={{ width: `${it.progress}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
